import { Flex } from '@iheartjane/reefer';
import { clsx } from 'clsx';

import styles from './carouselDots.module.css';

interface CarouselDotsProps {
  /** Index of the active slide (0-based). */
  activeIndex: number;

  /** Auto-advance interval in ms; drives the active-dot fill. Falsy → static filled dot, no fill animation. */
  autoAdvanceMs?: number;

  /** Additional CSS class name(s) applied to the dots row. */
  className?: string;

  /** Number of slides → number of dots. */
  count: number;

  /** Freezes the active-dot fill animation while auto-advance is paused. */
  isPaused?: boolean;

  /** Jump to a slide (parent updates activeIndex + resets the auto-advance timer). */
  onSelect: (index: number) => void;
}

/**
 * Pagination dots for `BannerCarousel`: one per slide, active dot is a pill with
 * a CSS fill that animates across `autoAdvanceMs`. Hidden for a single slide.
 */
export const CarouselDots = ({
  activeIndex,
  autoAdvanceMs,
  className,
  count,
  isPaused,
  onSelect,
}: CarouselDotsProps) => {
  // only show dots for multiple slides
  if (count <= 1) {
    return null;
  }

  return (
    <Flex
      alignItems="center"
      ariaLabel="Carousel pagination"
      className={clsx(styles['dots'], className)}
      data-testid="carousel-dots"
      justifyContent="center"
      role="group"
    >
      {Array.from({ length: count }, (_unused, index) => {
        const isActive = index === activeIndex;
        const hasFill = isActive && !!autoAdvanceMs;

        return (
          <button
            key={index}
            aria-current={isActive || undefined}
            aria-label={`Go to slide ${index + 1}`}
            className={clsx(styles['dot'], isActive && styles['active'])}
            onClick={() => onSelect(index)}
            type="button"
          >
            {hasFill && (
              // re-key on activeIndex so the fill restarts each slide
              <span
                key={activeIndex}
                className={styles['fill']}
                style={{
                  animationDuration: `${autoAdvanceMs}ms`,
                  animationPlayState: isPaused ? 'paused' : 'running',
                }}
              />
            )}
          </button>
        );
      })}
    </Flex>
  );
};
